<template>
  <div class="orders">
    <div class="orders-heading">
      <div class="orders-title">
        {{ $t('views.orders.title') }}
      </div>
      <div class="orders-subtitle">
        {{ $t('views.orders.subtitle') }}
      </div>
    </div>

    <div class="orders-filters-card">
      <div class="filters-inputs">
        <div class="filter-field">
          <label class="filter-label">
            {{ $t('views.orders.filters.order_number') }}
          </label>
          <v-text-field
            v-model="draftOrderNumber"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-magnify"
            :placeholder="$t('views.orders.filters.order_number_placeholder')"
            class="filter-order-number"
            @keyup.enter="fetchByFilters"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">
            {{ $t('views.orders.filters.status') }}
          </label>
          <custom-select
            v-model="draftStatus"
            :items="itemsSelectByStatus"
            sync-selected-from-value
            class="filter-select"
            :placeholder="$t('views.orders.filters.status_placeholder')"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">
            {{ $t('views.orders.filters.uf') }}
          </label>
          <custom-select
            v-model="draftUf"
            :items="itemsSelectByUf"
            sync-selected-from-value
            class="filter-select"
            :placeholder="$t('views.orders.filters.uf_placeholder')"
          />
        </div>
      </div>

      <div class="filters-actions">
        <v-btn
          outlined
          color="primary"
          class="filters-action-button"
          @click="clearFilters"
        >
          <v-icon left>
            mdi-refresh
          </v-icon>
          {{ $t('views.orders.filters.clear') }}
        </v-btn>

        <v-btn
          color="primary"
          class="filters-action-button"
          @click="fetchByFilters"
        >
          <v-icon left>
            mdi-filter-outline
          </v-icon>
          {{ $t('views.orders.filters.apply') }}
        </v-btn>
      </div>
    </div>

    <div
      v-if="hasOrders"
      class="cards-orders"
    >
      <card-order
        v-for="order in ordersList"
        :key="order.id"
        :order="order"
      />
    </div>
    <rectoplus-pagination
      v-if="hasOrders"
      :size="pagination.size"
      :page="pagination.page"
      @changed="changePage"
    />
    <div
      v-if="!hasOrders"
      class="orders-placeholder"
    >
      {{ $t('views.orders.placeholder') }}
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { OrderStatus } from '@/enums/OrderStatus';

import CardOrder from '@/components/cards/orders/CardOrder.vue';
import CustomSelect from '@/components/selects/CustomSelect.vue';
import RectoplusPagination from '@/components/pagination/RectoplusPagination.vue';

export default {
  name: 'Orders',
  components: {
    CardOrder,
    CustomSelect,
    RectoplusPagination,
  },
  data() {
    return {
      itemsSelectByStatus: [
        {
          text: this.$t('views.orders.filters.all'),
          value: null,
        },
        {
          text: this.$t('views.orders.filters.pending'),
          value: OrderStatus.PENDING,
        },
        {
          text: this.$t('views.orders.filters.paid'),
          value: OrderStatus.PAID,
        },
        {
          text: this.$t('views.orders.filters.cancelled'),
          value: OrderStatus.CANCELLED,
        },
      ],
      draftOrderNumber: '',
      appliedOrderNumber: '',
      draftUf: null,
      appliedUf: null,
      draftStatus: null,
      status: null,
      page: 1,
    };
  },
  computed: {
    ...mapGetters([
      'getOrders',
      'getBusiness',
      'getOrderAvailableUfs',
    ]),
    itemsSelectByUf() {
      return [
        {
          text: this.$t('views.orders.filters.all_ufs'),
          value: null,
        },
        ...this.getOrderAvailableUfs.map((uf) => ({
          text: uf,
          value: uf,
        })),
      ];
    },
    ordersList() {
      return this.getOrders?.data || [];
    },
    hasOrders() {
      return this.ordersList.length > 0;
    },
    pagination() {
      return {
        size: this.getOrders?.pagination_settings?.pages || 1,
        page: this.getOrders?.pagination_settings?.page || this.page,
      };
    },
  },
  mounted() {
    this.fetchOrderAvailableUfs({ business_id: this.getBusiness.id });
    this.changePage(1);
  },
  methods: {
    ...mapActions(['fetchOrders', 'fetchOrderAvailableUfs']),

    changePage(page) {
      this.page = page;
      this.fetchOrders({
        by_status: this.status,
        by_order_id: this.appliedOrderNumber || null,
        by_uf: this.appliedUf || null,
        business_id: this.getBusiness.id,
        page,
      });
    },

    fetchByFilters() {
      this.appliedUf = this.draftUf;
      this.status = this.draftStatus;
      this.appliedOrderNumber = this.draftOrderNumber.trim();
      this.changePage(1);
    },

    clearFilters() {
      this.draftOrderNumber = '';
      this.appliedOrderNumber = '';
      this.draftUf = null;
      this.appliedUf = null;
      this.draftStatus = null;
      this.status = null;
      this.changePage(1);
    },
  },
};

</script>
<style lang="scss" scoped>
  .orders {
    background-color: $app_background;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    padding: 0 2em 0 16em;

    .orders-heading {
      margin: 70px 0 32px 0;
    }

    .orders-title {
      font-size: 1.9em;
      font-weight: bold;
    }

    .orders-subtitle {
      color: rgba($color_dark, 0.72);
      font-size: 1em;
      margin-top: 8px;
    }

    .orders-filters-card {
      align-items: flex-end;
      background-color: $color_light;
      border-radius: 16px;
      box-shadow: $box_shadow_my_offereds;
      display: flex;
      justify-content: space-between;
      gap: 24px;
      margin-bottom: 24px;
      padding: 24px;
    }

    .filters-inputs {
      display: flex;
      flex: 1 1 auto;
      gap: 32px;
      width: auto;
    }

    .filter-field {
      display: flex;
      flex: 0 0 220px;
      flex-direction: column;
      gap: 8px;
      max-width: 220px;
      width: 220px;
    }

    .filter-label {
      color: rgba($color_dark, 0.72);
      font-size: 0.95em;
      font-weight: bold;
    }

    .filter-order-number,
    .filter-select {
      width: 100%;

      ::v-deep .v-input__slot,
      ::v-deep .custom-select-selected {
        background-color: $color_white;
      }
    }

    .filters-actions {
      align-self: center;
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      white-space: nowrap;
    }

    .filters-action-button {
      border-radius: 10px;
      height: 48px !important;
      text-transform: none;
    }

    .cards-orders {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .orders-placeholder {
      margin-top: 30px;
      text-align: center;
      font-size: 1.3em;
    }
  }

</style>
